import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { ConversationProvider, useConversation } from "Voice/elevenlabs";
import { useApi } from 'context/ApiProvider';
import toast from 'react-hot-toast';
import { getVoiceOptions, getVoiceSignedUrl, heartbeatVoiceSession, endVoiceSession, endVoiceSessionKeepAlive, voiceStartErrorMessage, VoiceOption, DEFAULT_VOICE_ID } from 'services/voiceService';
import { useViewerStore } from 'context/ViewerContext';
import { addPrinter, addVoiceAddition, applyClippingView, confirmPrint, createNewDesign, deleteDesignByQuery, dispatchCameraPan, dispatchCameraView, dispatchCameraZoom, dispatchEngravingParam, dispatchInsoleParam, dispatchProjectSetting, dispatchShoeSize, dispatchViewerMode, exportDesign, generateGCode, getAppMeasurement, getAppTheme, getDefaultPrinters, getSettingsState, listDesigns, listTemplates, loadDesignByQuery, loadTemplateByQuery, mirrorInsole, moveVoiceAddition, openDatabasePanel, openLoadTemplatePanel, openPatientPanel, openPropertyPanel, openSaveMenu, openSaveTemplatePanel, openScanPanel, openSettingsPanel, openTicketsPanel, removePrinter, removeVoiceAddition, requestAdditionsState, requestAlignState, requestEngravingState, requestInsoleState, requestPatientState, requestViewerModeState, savePatientPanel, saveSettingsPanel, sendSupportTicket, setAlignMode, setAppMeasurement, setAppTheme, setAutomaticParam, setModelCategory, setPatientParam, setPressureScan, setSettingsParam, setVoiceAdditionParam, startInterfaceTutorial, undoDesign, VOICE_SPEAK_LINE_EVENT, VOICE_SPEAK_PREFIX, withEngravingExit } from 'Voice/voiceCommands';
import {
    noteAgentAudioChunk,
    noteAgentInterruption,
    resetAgentAudioTimeline,
    setAgentSpeaking,
    setAgentOutputVolume,
} from 'Voice/voiceSpeechSync';
import { lockVoiceLang, noteTentativeUserSpeech, noteUserSpeech, resetVoiceLang } from 'Voice/voiceLanguage';
import './Chatbox.css';
import { useAuth } from 'context/AuthContext';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { useDesignStore } from 'context/DesignContext';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import { EXHIBITION_SKIP_TOUR_EVENT, EXHIBITION_START_TOUR_EVENT, requestExhibitionTour, skipExhibitionTour } from 'Exhibition/exhibitionEvents';
import { isExhibitionTourDesign } from 'Exhibition/exhibitionCapabilities';
import {
    TourContext,
    TourControlAction,
    abortExhibitionTour,
    handleTourControl,
    isTourRunning,
    startAppTutorial,
    startExhibitionTour,
    stopExhibitionTour,
    tourLanguage,
} from 'Exhibition/exhibitionTour';
import { requestTutorial, useTutorialStore } from 'Tutorial/tutorialStore';
import 'Pages/Exhibition/Exhibition.css';

const VOICE_STORAGE_KEY = "tsoles.agentVoiceId";

/**
 * Voice settings sent with every session, overriding the agent's own.
 *
 * Both are deliberately high. The assistant reads narration the app wrote, line
 * after line, and the one thing it must not do is give any single line a delivery
 * of its own — which is what the system prompt spends a paragraph forbidding.
 * Stability is what actually holds that: a low value lets the model reinterpret
 * each turn, and the same voice comes back warmer on one line than the next.
 * Similarity keeps it on the chosen voice rather than drifting off it.
 *
 * These only take effect because "voice stability" and "voice similarity" are
 * enabled as overridable in the agent's security settings. If either is switched
 * off there, the agent's own dashboard value is what plays and this is ignored.
 */
const VOICE_STABILITY = 0.5;
const VOICE_SIMILARITY_BOOST = 1;

/**
 * Whether a voice session is open, for the page rather than for a component.
 *
 * Deliberately module scope. Guards kept in refs only hold within one mounted
 * component, and the evidence from the service was two overlapping sessions
 * five seconds apart on the same page, each with its own audio — which is what
 * is heard as the assistant changing voice mid-sentence. Anything that survives
 * a remount has to live outside the component to be worth having.
 *
 * Cleared wherever a session ends, so a failed or finished session never leaves
 * the microphone permanently claimed.
 */
let voiceSessionClaimed = false;
/**
 * How long the microphone stays open after the walkthrough's closing line.
 *
 * Long enough for the line to land and for anyone who wants to carry on
 * talking to start, short enough that an unattended machine is not left
 * listening.
 */
const TUTORIAL_MIC_CLOSE_MS = 5000;
const HALO_WAVES = 16;
const HALO_POINTS = 160;
const HALO_BASE = 31.2;
const HALO_BANDS = 32;
const VOICE_LOOP_COPIES = 7;

const ChatboxInner: React.FC = () => {
    const api = useApi();
    const {
        isExhibition,
        exhibitionSession,
        updateExhibitionDesign,
        markExhibitionTourCompleted,
        dismissExhibitionTour,
    } = useAuth();
    const [starting, setStarting] = React.useState(false);
    const [voices, setVoices] = React.useState<VoiceOption[]>([]);
    // Read from storage on the very first render, not defaulted and corrected
    // later: a session can open before the voice list has loaded, and starting
    // from the built-in default meant it opened in a voice the user had not
    // chosen and only agreed with their choice afterwards.
    const [selectedVoiceId, setSelectedVoiceId] = React.useState<string>(
        () => {
            try {
                return localStorage.getItem(VOICE_STORAGE_KEY) || DEFAULT_VOICE_ID;
            } catch {
                return DEFAULT_VOICE_ID;
            }
        }
    );
    const voiceStripRef = React.useRef<HTMLDivElement | null>(null);
    const voiceStripReadyRef = React.useRef(false);
    const skipStripScrollRef = React.useRef(false);
    const stripScrollTimerRef = React.useRef<number | null>(null);
    const stripTapRef = React.useRef<{ x: number; y: number; scroll: number } | null>(null);
    const sideDragRef = React.useRef<{ pointerId: number; startX: number; startScroll: number; moved: boolean } | null>(null);
    const skipSideClickRef = React.useRef(false);
    const [voiceCenter, setVoiceCenter] = React.useState(0);
    const [level, setLevel] = React.useState(0);
    const haloPathEls = React.useRef<(SVGPathElement | null)[]>([]);
    const haloPhase = React.useRef(0);
    const haloLevel = React.useRef(0);
    const haloId = React.useId().replace(/:/g, "");
    const labelsBeforeVoiceRef = React.useRef<boolean | null>(null);
    const sessionIdRef = React.useRef(0);
    const liveSessionRef = React.useRef(false);
    /** This session was started with an opening message (not silenced). */
    const openingMessageRef = React.useRef(false);
    /** The assistant has made a sound in this session: the opening message has begun. */
    const agentHasSpokenRef = React.useRef(false);
    /**
     * The MediaStream the SDK opened for the live session. setMuted alone still
     * reports ambient volume to ElevenLabs (worklet zeros samples but not the
     * level), which keeps interrupting scripted TTS on a phone speaker. Disabling
     * these tracks sends real silence so the agent can finish SPEAK_EXACTLY lines.
     */
    const voiceMicStreamRef = React.useRef<MediaStream | null>(null);
    const heartbeatRef = React.useRef<number | null>(null);
    const apiRef = React.useRef(api);
    apiRef.current = api;
    // Filled in below, once the conversation exists. Held in a ref because the
    // tool registry is captured when the voice session starts and must not go
    // stale as the component re-renders.
    const tourContextRef = React.useRef<TourContext>(null as unknown as TourContext);
    // Set before a narration asks for a voice session, so the agent opens
    // silently rather than greeting over the first scripted line.
    const suppressGreetingRef = React.useRef(false);
    /** Bumped on every tour start request so a newer Replay cancels an older one. */
    const tourStartGenRef = React.useRef(0);

    const stopHeartbeat = React.useCallback(() => {
        if (heartbeatRef.current != null) {
            window.clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    }, []);

    const closeVoiceSession = React.useCallback((status = "ended", keepalive = false) => {
        // Released only when the conversation itself is over.
        //
        // The keepalive path does not end the conversation: it tells our own
        // backend the session is finished while the page is being hidden, and
        // the ElevenLabs conversation carries on. Releasing there handed the
        // microphone back while a session was still live, and the next caller
        // opened a second one beside it — which is the overlap that appeared a
        // minute and a half into a tour rather than at its start.
        if (!keepalive) {
            voiceSessionClaimed = false;
            lockedVoiceIdRef.current = null;
            const held = voiceMicStreamRef.current;
            voiceMicStreamRef.current = null;
            held?.getTracks().forEach((track) => {
                try {
                    track.enabled = true;
                    track.stop();
                } catch {
                    /* already ended */
                }
            });
        }
        const sessionId = sessionIdRef.current;
        if (!sessionId) return;
        sessionIdRef.current = 0;
        stopHeartbeat();
        if (keepalive) {
            endVoiceSessionKeepAlive(sessionId, status);
            return;
        }
        void endVoiceSession(apiRef.current, sessionId, status);
    }, [stopHeartbeat]);

    const clientTools = React.useMemo(() => {
        const tools: Record<string, (...args: any[]) => any> = withEngravingExit({
        rotateCamera: async ({ view }: { view: string }) => dispatchCameraView(view),
        zoomCamera: async ({ action, amount }: { action: string; amount?: number | string }) =>
            dispatchCameraZoom(action, amount),
        panCamera: async ({ action, amount }: { action: string; amount?: number | string }) =>
            dispatchCameraPan(action, amount),
        undo: async () => undoDesign(),
        mirrorInsole: async ({ side }: { side: string }) => mirrorInsole(side),
        setInsoleParam: async ({ foot, param, value, silent, more, changes }: { foot: string; param: string; value: number; silent?: string | boolean; more?: string | boolean; changes?: string }) =>
            dispatchInsoleParam(foot, param, Number(value), silent, more, changes),
        getInsoleState: async () => requestInsoleState(),
        setShoeSize: async ({ size, foot, more }: { size: string | number; foot?: string; more?: string | boolean }) =>
            dispatchShoeSize(size, foot || "both", more),
        getPatientState: async () => {
            try {
                return requestPatientState();
            } catch (error) {
                console.error("getPatientState failed", error);
                return JSON.stringify({ error: "Could not read patient state." });
            }
        },
        setViewerMode: async ({ setting, value }: { setting: string; value: string }) =>
            dispatchViewerMode(setting, value),
        setClippingView: async ({
            percent,
            verticalPercent,
            lateralPercent,
            enabled,
        }: {
            percent?: number | string;
            verticalPercent?: number | string;
            lateralPercent?: number | string;
            enabled?: string | boolean;
        }) => applyClippingView({ percent, verticalPercent, lateralPercent, enabled }),
        getViewerState: async () => requestViewerModeState(),
        openPanel: async ({ panel }: { panel: string }) => openPropertyPanel(panel),
        setEngravingParam: async ({ param, value }: { param: string; value: string | number }) =>
            dispatchEngravingParam(param, String(value)),
        getEngravingState: async () => requestEngravingState(),
        setProjectSetting: async ({ setting, value }: { setting: string; value: string | number }) =>
            dispatchProjectSetting(setting, String(value)),
        getAdditions: async () => requestAdditionsState(),
        addAddition: async ({ name, foot, height, hardness, silent }: { name: string; foot: string; height?: number | string; hardness?: string; silent?: string | boolean }) =>
            addVoiceAddition(api, name, foot, height, hardness, silent),
        removeAddition: async ({ foot, name, index, silent }: { foot: string; name?: string; index?: number; silent?: string | boolean }) =>
            removeVoiceAddition(foot, name, index === undefined ? undefined : Number(index), silent),
        moveAddition: async ({ foot, direction, amount, name, index }: { foot: string; direction: string; amount?: number | string; name?: string; index?: number }) =>
            moveVoiceAddition(foot, direction, amount, name, index === undefined ? undefined : Number(index)),
        setAdditionParam: async ({ foot, param, value, name, index, silent }: { foot: string; param: string; value: string | number; name?: string; index?: number; silent?: string | boolean }) =>
            setVoiceAdditionParam(foot, param, value, name, index === undefined ? undefined : Number(index), silent),
        openDatabase: async () => openDatabasePanel(),
        listDesigns: async ({ search, page }: { search?: string; page?: number | string }) =>
            listDesigns(api, search, page),
        loadDesign: async ({ id, name }: { id?: number | string; name?: string }) => loadDesignByQuery(api, id, name),
        deleteDesign: async ({ id, name, confirm }: { id?: number | string; name?: string; confirm?: string | boolean }) =>
            deleteDesignByQuery(api, id, name, confirm),
        newDesign: async ({ name }: { name?: string } = {}) => {
            const result = await createNewDesign(api, name);
            const designId = useSelectedDesignStore.getState().designId;
            if (isExhibition && designId > 0) updateExhibitionDesign(designId);
            return result;
        },
        openPatient: async ({ create, close }: { create?: string | boolean; close?: string | boolean } = {}) =>
            openPatientPanel(create, close),
        setPatientParam: async ({ param, value, foot, save, silent, apply }: { param: string; value: string | number; foot?: string; save?: string | boolean; silent?: string | boolean; apply?: string | boolean }) =>
            setPatientParam(api, param, value, foot, save, silent, apply),
        savePatient: async () => savePatientPanel(api),
        openScan: async ({ type }: { type?: string }) => openScanPanel(api, type),
        setPressureScan: async ({ action, mode }: { action: string; mode?: string }) => setPressureScan(action, mode),
        openLoadTemplate: async () => openLoadTemplatePanel(api),
        listTemplates: async () => listTemplates(api),
        loadTemplate: async ({ name, templateId, category }: { name?: string; templateId?: number | string; category?: string }) =>
            loadTemplateByQuery(api, name, templateId, category),
        setAlignMode: async ({ value }: { value?: string }) => setAlignMode(value),
        getAlignState: async () => requestAlignState(),
        openSaveMenu: async () => openSaveMenu(),
        exportDesign: async ({ format }: { format: string }) => exportDesign(format),
        saveAsTemplate: async ({ name, category, insoleColor, additionColor, save }: {
            name?: string;
            category?: string;
            insoleColor?: string;
            additionColor?: string;
            save?: string | boolean;
        }) => openSaveTemplatePanel(name, category, insoleColor, additionColor, save),
        setModelCategory: async ({ category }: { category: string }) => setModelCategory(category),
        setAutomaticParam: async ({ param, value, foot }: { param: string; value?: string | number; foot?: string }) =>
            setAutomaticParam(param, value, foot),
        getDefaultPrinters: async () => getDefaultPrinters(),
        openSettings: async () => openSettingsPanel(),
        getSettingsState: async () => getSettingsState(),
        addPrinter: async (args: Record<string, unknown> = {}) => addPrinter(args),
        removePrinter: async (args: Record<string, unknown> = {}) => removePrinter(args),
        setSettingsParam: async ({ param, value, foot, save, silent, printer, name, ip, setting }: {
            param: string;
            value?: string;
            foot?: string;
            save?: string | boolean;
            silent?: string | boolean;
            printer?: string;
            name?: string;
            ip?: string;
            setting?: string;
        }) => setSettingsParam(param, value, foot, save, silent, printer, name, ip, setting),
        saveSettings: async () => saveSettingsPanel(),
        generateGCode: async () => generateGCode(),
        confirmPrint: async ({ action, foot }: { action?: string; foot?: string }) => confirmPrint(action, foot),
        setMeasurement: async ({ value }: { value?: string }) => setAppMeasurement(value),
        getMeasurement: async () => getAppMeasurement(),
        setTheme: async ({ value }: { value?: string }) => setAppTheme(value),
        getTheme: async () => getAppTheme(),
        openTickets: async () => openTicketsPanel(),
        sendTicket: async (args: unknown) => sendSupportTicket(args),
        // The walkthrough has only ever been reachable from the Tutorial button.
        // The demonstration now ends by inviting the visitor to ask for it out
        // loud, so asking has to be a way in as well.
        startTutorial: async () => startInterfaceTutorial(),
        });

        if (!isExhibition) return tools;

        // Lets the visitor steer the scripted tour by voice: answering the offer
        // to be taught, or asking to move on, hear something again, or stop.
        tools.tourControl = async ({ action }: { action: string }) => {
            const normalized = String(action || "").trim() as TourControlAction;
            return handleTourControl(normalized, tourContextRef.current);
        };

        // Every Hub tool stays as it is. An exhibition session is an ordinary
        // session whose first design happens to be narrated: nothing a visitor
        // asks for afterwards is withheld, and asking to print builds real
        // G-code exactly as it does in hub mode.
        return tools;
    }, [api, isExhibition, updateExhibitionDesign]);

    const conversation = useConversation({
        clientTools,
        onMessage: (payload: { message?: string; source?: string; role?: string } | string) => {
            const text = typeof payload === "string" ? payload : payload?.message;
            const source = typeof payload === "string" ? "user" : payload?.source;
            const role = typeof payload === "string" ? "user" : payload?.role;
            if (!text) return;
            const fromUser = source === "user" || role === "user" || (!source && !role);
            if (!fromUser) {
                // Agent text means TTS is on its way (or already playing). Phones
                // often never surface output volume or audio events, so the tour
                // treats this as proof the assistant has started speaking.
                agentHasSpokenRef.current = true;
                setAgentSpeaking(true);
                return;
            }
            noteUserSpeech(text);
        },
        // Preferred path: the SDK delivers playback bytes here. onIncomingEvent
        // alone was missing chunks on some mobile WebSocket paths.
        onAudio: (base64Audio: string) => {
            if (!base64Audio) return;
            agentHasSpokenRef.current = true;
            noteAgentAudioChunk(base64Audio);
        },
        onInterruption: (event) => {
            noteAgentInterruption(
                (event as { event_id?: number } | undefined)?.event_id
            );
        },
        onModeChange: ({ mode }) => {
            if (mode === "speaking") {
                agentHasSpokenRef.current = true;
                setAgentSpeaking(true);
            } else {
                setAgentSpeaking(false);
            }
        },
        onIncomingEvent: (event: { type?: string; [key: string]: unknown }) => {
            // The assistant's speech, chunk by chunk. Its length is what the tour
            // times itself by: the level measured at the speaker is not reliable on
            // phones, and this is the same on every device.
            if (event?.type === "audio") {
                const audio = (event as { audio_event?: { audio_base_64?: string; event_id?: number } }).audio_event;
                if (audio?.audio_base_64) {
                    agentHasSpokenRef.current = true;
                    noteAgentAudioChunk(audio.audio_base_64, audio.event_id);
                }
            }
            if (event?.type === "interruption") {
                noteAgentInterruption(
                    (event as { interruption_event?: { event_id?: number } }).interruption_event?.event_id
                );
            }
            if (event?.type === "tentative_user_transcript") {
                const text = (event as { tentative_user_transcription_event?: { user_transcript?: string } })
                    .tentative_user_transcription_event?.user_transcript;
                if (text) noteTentativeUserSpeech(text);
            }
            if (event?.type === "user_transcript") {
                const text = (event as { user_transcription_event?: { user_transcript?: string } })
                    .user_transcription_event?.user_transcript;
                if (text) noteUserSpeech(text);
            }
        },
        onError: (message) => {
            toast.error(voiceStartErrorMessage(message), { id: "voice-assistant-error", duration: 6000 });
        },
        onUnhandledClientToolCall: (tool) => {
            toast.error(`Voice tool "${tool?.tool_name || "unknown"}" is not registered in the app.`);
        },
    });
    const conversationRef = React.useRef(conversation);
    conversationRef.current = conversation;

    tourContextRef.current = {
        api,
        // The script owns the wording; the agent only reads it back.
        speak: (text: string) => {
            if (conversationRef.current.status !== "connected") return false;
            try {
                conversationRef.current.sendUserMessage(`${VOICE_SPEAK_PREFIX} ${text}`);
                return true;
            } catch {
                return false;
            }
        },
        nudgeAgent: () => {
            try {
                conversationRef.current.sendUserActivity();
            } catch {
                /* session already closed */
            }
        },
        isConnected: () => conversationRef.current.status === "connected",
        onFinished: () => markExhibitionTourCompleted(),
        // The visitor asked it to stop and then declined the other half. Same
        // outcome as pressing Skip: the microphone closes and the controls stay
        // away, rather than the tour quietly counting itself as completed.
        onDismissed: () => {
            dismissExhibitionTour();
            endLiveVoiceRef.current("skipped");
        },
        // Exhibition sessions suppress the agent's opening greeting below.
        expectsGreeting: !isExhibition && !suppressGreetingRef.current,
        greetingPending: () => openingMessageRef.current && !agentHasSpokenRef.current,
        setMicMuted: (muted: boolean) => {
            // Disable the live tracks first. SDK setMuted alone still forwards a
            // non-zero VAD level from ambient noise, which interrupts every
            // scripted line on speakerphone.
            voiceMicStreamRef.current?.getAudioTracks().forEach((track) => {
                track.enabled = !muted;
            });
            try {
                conversationRef.current.setMuted(muted);
            } catch {
                /* session already closed */
            }
        },

    };

    const endLiveVoiceRef = React.useRef<(status?: string) => void>(() => {});
    /** Pending close of the microphone after the walkthrough's last line. */
    const tutorialMicCloseRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    // Read when the walkthrough finishes rather than captured when it starts,
    // so the decision is made against the session as it stands by then.
    const isExhibitionRef = React.useRef(isExhibition);
    isExhibitionRef.current = isExhibition;

    const endLiveVoice = React.useCallback((status = "ended") => {
        liveSessionRef.current = false;
        // The conversation is ended first and the claim released after, not the
        // other way round. Releasing first opens a window in which the session
        // is still alive on the service while anything asking for the microphone
        // is allowed through — which is how two sessions ended up side by side.
        try {
            conversationRef.current.endSession();
        } catch {
            /* already closed */
        }
        closeVoiceSession(status);
        if (labelsBeforeVoiceRef.current === false) {
            dispatchViewerMode("labels", "off");
        }
        labelsBeforeVoiceRef.current = null;
        useViewerStore.getState().setVoiceSessionActive(false);
    }, [closeVoiceSession]);

    endLiveVoiceRef.current = endLiveVoice;

    // Unmounting with a session open would leave the page-level claim set with
    // nothing left to release it, and the microphone could never be opened again
    // without a reload. Runs on unmount only.
    React.useEffect(() => () => {
        if (tutorialMicCloseRef.current) {
            clearTimeout(tutorialMicCloseRef.current);
            tutorialMicCloseRef.current = null;
        }
        if (voiceSessionClaimed || conversationRef.current.status !== "disconnected") {
            endLiveVoiceRef.current("ended");
        }
        voiceSessionClaimed = false;
    }, []);

    const connected = conversation.status === "connected";
    // The guided tour mutes the microphone while a scripted line plays. That is
    // reported in the tooltip only: slashing the icon made a live, talking
    // assistant look switched off, which is what the button's icon means
    // everywhere else in the app.
    const micMuted = Boolean(conversation.isMuted);
    const connecting = starting;
    const speaking = connected && conversation.isSpeaking;
    const clippingActive = useViewerStore((state) => state.clipping);

    React.useEffect(() => {
        if (!connected) {
            setAgentSpeaking(false);
            setAgentOutputVolume(0);
            resetAgentAudioTimeline();
            return;
        }
        if (speaking) agentHasSpokenRef.current = true;
        setAgentSpeaking(Boolean(speaking));
    }, [connected, speaking]);

    React.useEffect(() => {
        let cancelled = false;
        getVoiceOptions(api).then((next) => {
            if (cancelled) return;
            setVoices(next);
            if (!next.length) return;
            // Only corrected when the stored choice is not a voice this agent
            // offers. It used to be overwritten with the built-in default every
            // time the list arrived, which quietly discarded the user's setting.
            setSelectedVoiceId((current) => {
                if (next.some((voice) => voice.id === current)) return current;
                const fallback = next.find((voice) => voice.id === DEFAULT_VOICE_ID) || next[0];
                try {
                    localStorage.setItem(VOICE_STORAGE_KEY, fallback.id);
                } catch {
                    /* private mode */
                }
                return fallback.id;
            });
        });
        return () => {
            cancelled = true;
        };
    }, [api]);

    React.useEffect(() => {
        if (!connected || !sessionIdRef.current) {
            stopHeartbeat();
            return;
        }
        let cancelled = false;
        const ping = async () => {
            if (!sessionIdRef.current) return;
            const result = await heartbeatVoiceSession(api, sessionIdRef.current);
            if (cancelled) return;
            if (result === "blocked") {
                toast.error("Voice assistant access was blocked.");
                endLiveVoice("interrupted");
            }
        };
        void ping();
        heartbeatRef.current = window.setInterval(() => {
            void ping();
        }, 15000);
        return () => {
            cancelled = true;
            stopHeartbeat();
        };
    }, [connected, api, stopHeartbeat, endLiveVoice]);

    React.useEffect(() => {
        if (connected) liveSessionRef.current = true;
        if (connected || starting || conversation.status === "connecting" || !sessionIdRef.current) return;
        if (!liveSessionRef.current) return;
        liveSessionRef.current = false;
        // Told to the service as well as to our own backend. This used to report
        // the session as finished on our side only, so a half-open conversation
        // could stay alive on ElevenLabs while the claim was handed back here.
        try {
            conversationRef.current.endSession();
        } catch {
            /* already closed */
        }
        closeVoiceSession("interrupted");
    }, [connected, starting, conversation.status, closeVoiceSession]);

    // A parameter tour needs real silence between parameters, which only exists
    // between turns. The viewer asks for the next sentence once its gap has
    // elapsed, and it arrives as a user message the agent is told to read back
    // verbatim; that starts a fresh turn the camera can lock onto.
    React.useEffect(() => {
        if (!connected) return;
        const onSpeakLine = (event: Event) => {
            const text = (event as CustomEvent<{ text?: string }>).detail?.text;
            if (!text) return;
            try {
                conversationRef.current.sendUserMessage(`${VOICE_SPEAK_PREFIX} ${text}`);
            } catch {
                /* session closed mid-tour */
            }
        };
        document.addEventListener(VOICE_SPEAK_LINE_EVENT, onSpeakLine);
        return () => document.removeEventListener(VOICE_SPEAK_LINE_EVENT, onSpeakLine);
    }, [connected]);

    React.useEffect(() => {
        const onHide = () => closeVoiceSession("ended", true);
        window.addEventListener("pagehide", onHide);
        window.addEventListener("beforeunload", onHide);
        return () => {
            window.removeEventListener("pagehide", onHide);
            window.removeEventListener("beforeunload", onHide);
        };
    }, [closeVoiceSession]);

    // A tab the user has left keeps no voice session open.
    //
    // On a phone, Chrome freezes a background tab. Its microphone stream stops in
    // the middle of the session and nothing more is sent, but the session stays
    // open on ElevenLabs until it times out much later (code 1006) — still holding
    // the microphone, so a tab opened next to it could not be heard, and the tour
    // it was running stalled where it stood. So the session is ended as soon as
    // the page is frozen, or hidden on a touch device. Coming back, tapping the
    // microphone opens a fresh one (and, on the demonstration, the tour again).
    React.useEffect(() => {
        const endIfLive = () => {
            if (conversationRef.current.status === "disconnected") return;
            stopExhibitionTour();
            useTutorialStore.getState().reset();
            endLiveVoiceRef.current("ended");
        };
        const onVisibility = () => {
            if (document.visibilityState !== "hidden") return;
            if (!window.matchMedia?.("(pointer: coarse)").matches) return;
            endIfLive();
        };
        document.addEventListener("freeze", endIfLive);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            document.removeEventListener("freeze", endIfLive);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    React.useEffect(() => {
        if (!connected) {
            setLevel(0);
            haloLevel.current = 0;
            haloPhase.current = 0;
            return;
        }

        let frame = 0;
        const tick = () => {
            const input = safeNumber(conversation.getInputVolume);
            const output = safeNumber(conversation.getOutputVolume);
            // Always publish output level so camera sync can hear TTS even when
            // isSpeaking flickers or arrives late on a slow network.
            setAgentOutputVolume(output);
            if (output >= 0.06) agentHasSpokenRef.current = true;
            if (output >= 0.06) setAgentSpeaking(true);
            else if (!speaking) setAgentSpeaking(false);

            const raw = speaking || output >= 0.06
                ? Math.max(output, 0.12)
                : Math.max(input, conversation.isListening ? 0.03 : 0);
            const boosted = Math.min(1, Math.pow(raw, 0.48) * 1.35);
            haloLevel.current = haloLevel.current * 0.32 + boosted * 0.68;
            setLevel(haloLevel.current);
            haloPhase.current += 0.04 + haloLevel.current * 0.16;

            const bands = sampleBands(
                speaking || output >= 0.06
                    ? conversation.getOutputByteFrequencyData
                    : conversation.getInputByteFrequencyData
            );
            for (let wave = 0; wave < HALO_WAVES; wave += 1) {
                haloPathEls.current[wave]?.setAttribute(
                    "d",
                    buildHaloWave(wave, haloLevel.current, bands, haloPhase.current)
                );
            }
            frame = window.requestAnimationFrame(tick);
        };
        frame = window.requestAnimationFrame(tick);
        return () => window.cancelAnimationFrame(frame);
    }, [connected, speaking, conversation.isListening]);

    const startVoiceInFlightRef = React.useRef<Promise<void> | null>(null);

    // Whether anything is live or on its way, from the SDK rather than from our
    // own bookkeeping. The flag below only has to cover the moment between
    // asking for a session and the SDK reporting one.
    /** The voice this session opened with, held until it closes. */
    const lockedVoiceIdRef = React.useRef<string | null>(null);

    const voiceSessionExists = () => {
        const status = conversationRef.current.status;
        return status === "connected" || status === "connecting";
    };

    const startVoice = async (voiceId = selectedVoiceId) => {
        // Asking for the microphone cancels a close the walkthrough left
        // pending, so the session just opened is not shut a moment later.
        if (tutorialMicCloseRef.current) {
            clearTimeout(tutorialMicCloseRef.current);
            tutorialMicCloseRef.current = null;
        }
        // A start already on its way is awaited rather than duplicated. Two
        // callers can want the microphone at once — the exhibition opens it as
        // the design id becomes known, and the narration asks again when the
        // design has loaded.
        if (startVoiceInFlightRef.current) return startVoiceInFlightRef.current;
        // Already live: nothing to do.
        if (conversationRef.current.status === "connected") return;
        // Connecting / claimed without an in-flight promise used to return at
        // once. The exhibition tour treated that as "start finished", saw that
        // the socket was still down, gave up, and never retried — while the
        // early-mic welcome had already played, so the visitor was left with an
        // open microphone and no narration. Wait for that handshake instead.
        if (voiceSessionClaimed || voiceSessionExists()) {
            try {
                await waitUntil(
                    () =>
                        conversationRef.current.status === "connected" ||
                        conversationRef.current.status === "disconnected",
                    15000,
                    "Could not reach the server. Check your connection and try again."
                );
            } catch {
                /* caller checks status */
            }
            return;
        }

        const run = startVoiceOnce(voiceId);
        startVoiceInFlightRef.current = run;
        try {
            await run;
        } finally {
            startVoiceInFlightRef.current = null;
        }
    };

    const startVoiceOnce = async (voiceId: string) => {
        // Nothing has been heard from this session yet.
        agentHasSpokenRef.current = false;
        resetAgentAudioTimeline();
        // Pinned for the whole session: every later read of the voice comes from
        // here, so nothing that changes while the session is open can move it.
        lockedVoiceIdRef.current = voiceId;
        const voice = voices.find((item) => item.id === voiceId);
        // Claimed before anything can await, so no second caller can slip in.
        voiceSessionClaimed = true;
        setStarting(true);
        try {
            const mediaDevices = navigator.mediaDevices;
            if (!mediaDevices?.getUserMedia) {
                const viaHttp = typeof window !== "undefined" && window.isSecureContext === false;
                throw new Error(
                    viaHttp
                        ? "Microphone needs a secure page. Open the app with https://, not http://."
                        : "This browser cannot access the microphone."
                );
            }
            const stream = await mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((track) => track.stop());
            useViewerStore.getState().setVoiceSessionActive(true);
            resetVoiceLang();
            // resetVoiceLang clears the lock, so an exhibition session re-pins
            // English here. Otherwise anything that reopens the session
            // mid-run — changing the voice, for instance — handed the language
            // back to the transcriber.
            if (isExhibition) lockVoiceLang("en");
            // The id, not the looked-up entry: selectedVoiceId holds the
            // configured default from the very first render, while the list it
            // would be looked up in arrives later. Passing the entry meant an
            // early session asked for no voice at all and the server chose one,
            // which is what made the assistant start in a different voice.
            const { signedUrl, sessionId } = await getVoiceSignedUrl(api, voice?.agentId, lockedVoiceIdRef.current ?? voiceId);
            sessionIdRef.current = sessionId;
            const overrides: Record<string, unknown> = {};
            // Always sent, and never conditional on the voice list having
            // loaded. The agent's own voice is not the one configured as the
            // default, so omitting this — which is what happened when a tour
            // auto-started before getVoiceOptions resolved — used the agent's.
            overrides.tts = {
                voiceId: lockedVoiceIdRef.current || voiceId || DEFAULT_VOICE_ID,
                stability: VOICE_STABILITY,
                similarityBoost: VOICE_SIMILARITY_BOOST,
            };
            // Only silenced when a narration is about to speak over it.
            //
            // On the demonstration design that is always: opening the microphone
            // there puts the visitor into the tour, whether it is their first
            // time or they are coming back after skipping it. On any other design
            // — the copy they go on to make — the assistant greets them the
            // ordinary way, which every exhibition session used to be denied.
            const onDemoDesign =
                isExhibition &&
                isExhibitionTourDesign(
                    useSelectedDesignStore.getState().designId,
                    exhibitionSession?.demoDesignId
                );
            if (suppressGreetingRef.current || onDemoDesign) {
                // Exhibition demo and tutorials own their opening line through
                // SPEAK_EXACTLY. A session firstMessage used to play during load
                // and then race the tour — on phones that often left an open mic
                // after the welcome with no narration following.
                overrides.agent = { firstMessage: "" };
            }
            if (isExhibition) {
                // Pin the agent to English as well as the script.
                //
                // Locking the client's own language only fixed the text this
                // app writes. The agent has its own language_detection tool and
                // switches on what it hears — and with the microphone open at a
                // stand, room noise and its own voice through the speakers were
                // enough to make it answer, and read scripted lines, in another
                // language.
                overrides.agent = { ...(overrides.agent as object), language: "en" };
            }

            // An empty override is the only way the opening message is skipped;
            // without one the agent speaks its own greeting.
            openingMessageRef.current =
                (overrides.agent as { firstMessage?: string } | undefined)?.firstMessage !== "";

            // Capture the stream the SDK opens so scripted lines can disable the
            // tracks (real silence) instead of relying on setMuted alone.
            const mediaDevicesRef = navigator.mediaDevices;
            const originalGetUserMedia = mediaDevicesRef.getUserMedia.bind(mediaDevicesRef);
            mediaDevicesRef.getUserMedia = async (constraints) => {
                const live = await originalGetUserMedia(constraints);
                // Any getUserMedia during session start is the voice mic.
                voiceMicStreamRef.current?.getTracks().forEach((track) => {
                    try {
                        track.stop();
                    } catch {
                        /* replaced */
                    }
                });
                voiceMicStreamRef.current = live;
                return live;
            };
            try {
                conversation.startSession({
                    signedUrl,
                    clientTools,
                    overrides: Object.keys(overrides).length ? overrides : undefined,
                    // Selects the guided-tour persona on the shared agent without a
                    // second agent, so the tour and the design assistant are one
                    // continuous session.
                    dynamicVariables: {
                        mode: isExhibition ? "exhibition" : "hub",
                        tour_language: tourLanguage(),
                    },
                });
                await waitUntil(
                    () => conversationRef.current.status === "connected",
                    12000,
                    "Could not reach the server. Check your connection and try again."
                );
                // Demonstration design: stay deaf until the tour owns the mic.
                // Voice may connect while the model is still loading; an open
                // mic here would hear booth noise before any SPEAK_EXACTLY.
                if (onDemoDesign) {
                    tourContextRef.current.setMicMuted?.(true);
                }
            } finally {
                mediaDevicesRef.getUserMedia = originalGetUserMedia;
            }
            liveSessionRef.current = true;
        } catch (error: any) {
            // Nothing is open, so the next attempt must be allowed through.
            voiceSessionClaimed = false;
            try {
                conversation.endSession();
            } catch {
                /* session may not have started */
            }
            closeVoiceSession("failed");
            toast.error(voiceStartErrorMessage(error), { id: "voice-assistant-error", duration: 6000 });
            if (labelsBeforeVoiceRef.current === false) {
                dispatchViewerMode("labels", "off");
            }
            labelsBeforeVoiceRef.current = null;
            useViewerStore.getState().setVoiceSessionActive(false);
        } finally {
            setStarting(false);
        }
    };

    const onToggleVoice = async () => {
        if (connected) {
            // Stop any scripted narration with the session. On the exhibition
            // demo design this is not Skip: Skip dismisses the tour for good,
            // while closing the mic and opening it again must be able to replay
            // from the start on the insole.
            stopExhibitionTour();
            useTutorialStore.getState().reset();
            endLiveVoice("ended");
            return;
        }
        await startVoice();
        // Back into the tour, not into a bare assistant. The microphone being
        // opened by hand on the demonstration design is the visitor asking to be
        // shown it from the beginning — including after a previous pass left
        // the scene on the sandal.
        if (
            isExhibition &&
            isExhibitionTourDesign(
                useSelectedDesignStore.getState().designId,
                exhibitionSession?.demoDesignId
            )
        ) {
            requestExhibitionTour(true);
        }
    };

    const onSelectVoice = async (voiceId: string) => {
        // The voice is fixed once the microphone is open. Changing it used to
        // close the session and open another one in the new voice, which is a
        // second session and a second voice in the middle of whatever was being
        // said. It is chosen before the microphone opens; from then on the
        // session keeps the voice it started with.
        if (voiceSessionExists()) return;
        setSelectedVoiceId(voiceId);
        try {
            localStorage.setItem(VOICE_STORAGE_KEY, voiceId);
        } catch {
            /* private mode */
        }
    };

    const startVoiceRef = React.useRef(startVoice);
    startVoiceRef.current = startVoice;

    React.useEffect(() => {
        if (!isExhibition) return;
        const onStartTour = async (event: Event) => {
            const force = Boolean((event as CustomEvent<{ force?: boolean }>).detail?.force);
            // Applies to Replay as well: forcing the tour onto a design it does
            // not describe would narrate pressure and bones that are not there.
            if (!isExhibitionTourDesign(
                useSelectedDesignStore.getState().designId,
                exhibitionSession?.demoDesignId
            )) return;
            if (!force && exhibitionSession?.tourDismissed) return;
            // A second auto-fire while the first is still connecting would start
            // the script twice over one session.
            if (!force && isTourRunning()) return;

            const startGen = ++tourStartGenRef.current;

            // Which narration Replay is repeating. Read before anything is torn
            // down, because the walkthrough and the demonstration share these
            // controls: pressing Replay while the walkthrough is speaking has to
            // repeat the walkthrough, not start the tour over the top of it.
            const replayingTutorial =
                force && useTutorialStore.getState().narration === "tutorial";

            // Replay (force): cut the script and hard-restart the voice session.
            // sendUserActivity alone does not stop mid-utterance TTS, and a
            // SPEAK_EXACTLY sent while the agent is still talking is ignored —
            // which is why Replay mid-speech looked like it did nothing.
            if (force) {
                abortExhibitionTour();
                suppressGreetingRef.current = true;
                noteAgentInterruption();
                resetAgentAudioTimeline();
                setAgentSpeaking(false);
                setAgentOutputVolume(0);
                if (conversationRef.current.status !== "disconnected") {
                    endLiveVoiceRef.current("ended");
                    // Let the socket finish closing before opening a new one.
                    await new Promise((resolve) => window.setTimeout(resolve, 120));
                }
                if (startGen !== tourStartGenRef.current) return;
                await startVoiceRef.current();
            } else if (conversationRef.current.status !== "connected") {
                // A failed microphone probe on the entry screen is not a reason to
                // give up silently: startVoice asks again and, if it still fails,
                // says why (denied, or an insecure http:// page, which is where
                // navigator.mediaDevices is missing entirely). Returning here
                // instead left the visitor with a mute app and no explanation.
                suppressGreetingRef.current = true;
                await startVoiceRef.current();
            }
            if (startGen !== tourStartGenRef.current) return;
            if (conversationRef.current.status !== "connected") {
                suppressGreetingRef.current = false;
                return;
            }

            // The restart above is what Replay needs whichever narration it is
            // repeating — the script is the only part that differs. Routed back
            // through the tutorial store so the walkthrough keeps the single
            // entry point that owns starting it.
            if (replayingTutorial) {
                requestTutorial();
                return;
            }

            useTutorialStore.getState().setRunning(true, "tour");
            try {
                await startExhibitionTour({
                    ...tourContextRef.current,
                    // Whole demonstration is SPEAK_EXACTLY-only — booth noise
                    // must not barge in between lines (especially sandal rebuild).
                    deafMic: true,
                });
            } finally {
                if (startGen === tourStartGenRef.current) {
                    useTutorialStore.getState().setRunning(false);
                    // Cleared only now: the next session the visitor opens by hand is
                    // an ordinary one and should greet them.
                    suppressGreetingRef.current = false;
                }
            }
        };
        document.addEventListener(EXHIBITION_START_TOUR_EVENT, onStartTour as EventListener);
        return () => {
            document.removeEventListener(EXHIBITION_START_TOUR_EVENT, onStartTour as EventListener);
        };
    }, [
        isExhibition,
        connected,
        exhibitionSession?.tourDismissed,
        exhibitionSession?.demoDesignId,
        dismissExhibitionTour,
        endLiveVoice,
    ]);

    // A visitor can create or open another design while the tour is speaking.
    // The remaining steps would describe a design that is no longer on screen,
    // so the tour ends there — the assistant itself stays connected.
    const selectedDesignId = useSelectedDesignStore((state) => state.designId);
    React.useEffect(() => {
        if (!isExhibition || !isTourRunning()) return;
        if (isExhibitionTourDesign(selectedDesignId, exhibitionSession?.demoDesignId)) return;
        stopExhibitionTour();
    }, [isExhibition, selectedDesignId, exhibitionSession?.demoDesignId]);

    // Skip, for either narration. Deliberately outside the exhibition-gated
    // effect below: that gate meant pressing Skip during a tutorial reached
    // nothing, so the assistant kept talking with the microphone open.
    React.useEffect(() => {
        const onSkipTour = () => {
            // Stop the script, close the session, put the controls away.
            stopExhibitionTour();
            endLiveVoiceRef.current("skipped");
            useTutorialStore.getState().reset();
            if (isExhibition) dismissExhibitionTour();
        };
        document.addEventListener(EXHIBITION_SKIP_TOUR_EVENT, onSkipTour);
        return () => document.removeEventListener(EXHIBITION_SKIP_TOUR_EVENT, onSkipTour);
    }, [isExhibition, dismissExhibitionTour]);

    // The Tutorial button, which any user can press on any design. Watches a
    // counter rather than a flag so pressing it again after one has finished
    // still registers.
    const tutorialRequest = useTutorialStore((state) => state.requestCount);
    React.useEffect(() => {
        if (!tutorialRequest) return;
        void (async () => {
            if (conversationRef.current.status !== "connected") {
                suppressGreetingRef.current = true;
                await startVoiceRef.current();
            }
            if (conversationRef.current.status !== "connected") {
                suppressGreetingRef.current = false;
                return;
            }
            useTutorialStore.getState().setRunning(true, "tutorial");
            await startAppTutorial({
                ...tourContextRef.current,
                // Same as the demonstration: SPEAK_EXACTLY throughout, so the
                // microphone stays shut for the whole walkthrough and between
                // its lines. Room noise must not register as a turn and cut a
                // line short.
                deafMic: true,
                // An exhibition session is pinned to English; the walkthrough must
                // not hand the language back to the room.
                keepLanguage: isExhibition,
                onFinished: () => {
                    useTutorialStore.getState().markCompleted();
                    // The walkthrough is over and nobody asked to keep talking,
                    // so the microphone should not be left open on its own. A
                    // few seconds first, so the closing line has landed.
                    //
                    // Not at a stand: there the open microphone is the booth's,
                    // not this walkthrough's, and closing it would leave the
                    // stand deaf until someone restarted the session by hand.
                    if (isExhibitionRef.current) return;
                    if (tutorialMicCloseRef.current) clearTimeout(tutorialMicCloseRef.current);
                    tutorialMicCloseRef.current = setTimeout(() => {
                        tutorialMicCloseRef.current = null;
                        endLiveVoiceRef.current("ended");
                    }, TUTORIAL_MIC_CLOSE_MS);
                },
                onDismissed: () => {
                    useTutorialStore.getState().reset();
                    endLiveVoiceRef.current("skipped");
                },
            });
            // Cleared once the walkthrough is done, so the next session the user
            // opens by hand greets them as usual. Left set, it silenced every
            // session after the first tutorial.
            suppressGreetingRef.current = false;
        })();
    }, [tutorialRequest]);

    // Opening the demonstration design starts the tour: the microphone is asked
    // for and the assistant begins speaking, with no button to press.
    //
    // Expressed as a condition rather than as a one-shot event on purpose. It
    // used to be dispatched from MainApp on a timer, which meant the whole thing
    // depended on this component's listener being attached at that exact moment
    // — and a missed event left the visitor with a silent app. A condition
    // cannot be missed: whenever it becomes true, this runs.

    // Working the model counts as being present.
    //
    // The agent takes its turn after a fixed silence, and a clinician studying
    // the insole — orbiting it, zooming, opening a panel — is silent for far
    // longer than that while being anything but idle. The result was the
    // assistant breaking in to ask whether they were still there, over a
    // design the user was in the middle of inspecting.
    //
    // sendUserActivity resets that timer. Deliberately driven by interaction
    // rather than by mere pointer movement: a mouse drifting across the page is
    // not work, and treating it as work would disable the silence timeout
    // altogether. A drag only counts while a button is held, which is what
    // orbiting the camera actually is.
    //
    // Two guards matter. It is never sent while the agent is speaking, because
    // sendUserActivity cuts leftover TTS mid-word, and never during scripted
    // narration, which paces its own nudges and must not be interrupted by the
    // visitor touching the screen.
    const speakingRef = React.useRef(false);
    speakingRef.current = speaking;
    React.useEffect(() => {
        if (!connected) return;
        let lastSent = 0;
        const THROTTLE_MS = 3000;
        const markActive = () => {
            if (speakingRef.current) return;
            if (useTutorialStore.getState().running || isTourRunning()) return;
            const now = Date.now();
            if (now - lastSent < THROTTLE_MS) return;
            lastSent = now;
            try {
                conversationRef.current.sendUserActivity();
            } catch {
                /* session already closed */
            }
        };
        const onPointerMove = (event: PointerEvent) => {
            // Held button only: this is the camera being dragged, not the
            // pointer passing over the viewer.
            if (event.buttons === 0) return;
            markActive();
        };
        document.addEventListener("pointerdown", markActive, true);
        document.addEventListener("pointermove", onPointerMove, true);
        document.addEventListener("wheel", markActive, { capture: true, passive: true });
        document.addEventListener("touchstart", markActive, { capture: true, passive: true });
        document.addEventListener("touchmove", markActive, { capture: true, passive: true });
        document.addEventListener("keydown", markActive, true);
        return () => {
            document.removeEventListener("pointerdown", markActive, true);
            document.removeEventListener("pointermove", onPointerMove, true);
            document.removeEventListener("wheel", markActive, true);
            document.removeEventListener("touchstart", markActive, true);
            document.removeEventListener("touchmove", markActive, true);
            document.removeEventListener("keydown", markActive, true);
        };
    }, [connected]);

    // Labels come on for a voice session, once there is a design to label.
    //
    // Both conditions matter and either can arrive first. Turning them on with
    // the session alone asked the viewer to build labels from parameters that
    // had not loaded, which threw; leaving it in the session start meant the
    // exhibition — which opens the microphone while the design is still loading
    // — never got them at all.
    //
    // The "is there a design" half is read as the design itself being present,
    // not as designLoaded. That flag is a one-shot trigger: the viewer consumes
    // it and immediately writes it back to false. So it is only ever true for
    // the moment the design lands, and in ordinary use the microphone is opened
    // long after that — the condition was already false by then and the labels
    // never came on at all. It worked in the exhibition purely because there
    // the microphone is open before the design arrives.
    const designPresent = useDesignStore((state) => state.insoleDesign != null);
    const labelsForVoiceRef = React.useRef(false);
    React.useEffect(() => {
        if (!designPresent || !connected) {
            if (!connected) labelsForVoiceRef.current = false;
            return;
        }
        if (labelsForVoiceRef.current) return;
        labelsForVoiceRef.current = true;
        if (labelsBeforeVoiceRef.current === null) {
            labelsBeforeVoiceRef.current = useViewerStore.getState().liveMode;
        }
        dispatchViewerMode("labels", "on");
    }, [designPresent, connected]);

    // Warm the voice session while the demonstration design loads.
    //
    // Safe because firstMessage is empty on the demo design and the mic stays
    // muted until the tour starts. The old bug was speaking a welcome during
    // load and then racing SPEAK_EXACTLY — not opening the socket early.
    React.useEffect(() => {
        if (!isExhibition || exhibitionSession?.tourDismissed) return;
        if (!isExhibitionTourDesign(selectedDesignId, exhibitionSession?.demoDesignId)) return;
        if (conversationRef.current.status === "connected") return;
        if (voiceSessionClaimed || startVoiceInFlightRef.current) return;
        void startVoiceRef.current();
    }, [
        isExhibition,
        selectedDesignId,
        exhibitionSession?.demoDesignId,
        exhibitionSession?.tourDismissed,
    ]);

    // One path only: when the demonstration design is fetched AND the mic is
    // ready, run the tour. Voice may already be connected from the warm-up
    // above; we still wait for the design so SPEAK_EXACTLY never races load.
    const hasFetchedDesign = useDesignStore((state) => state.insoleDesign != null);
    const autoStartedRef = React.useRef(false);
    React.useEffect(() => {
        if (!isExhibitionTourDesign(selectedDesignId, exhibitionSession?.demoDesignId)) {
            autoStartedRef.current = false;
            return;
        }
        if (!isExhibition || exhibitionSession?.tourDismissed) return;
        if (!hasFetchedDesign) return;
        if (autoStartedRef.current || isTourRunning()) return;

        autoStartedRef.current = true;
        void (async () => {
            const voiceIsConnected = () => conversationRef.current.status === "connected";
            for (let attempt = 0; attempt < 40; attempt += 1) {
                if (voiceIsConnected()) break;
                await startVoiceRef.current();
                if (voiceIsConnected()) break;
                await new Promise((resolve) => window.setTimeout(resolve, 500));
            }
            if (!voiceIsConnected()) {
                autoStartedRef.current = false;
                return;
            }
            if (isTourRunning()) return;
            useTutorialStore.getState().setRunning(true, "tour");
            try {
                const tourCtx = { ...tourContextRef.current, deafMic: true };
                let finished = await startExhibitionTour(tourCtx);
                if (!finished && voiceIsConnected() && !isTourRunning()) {
                    await new Promise((resolve) => window.setTimeout(resolve, 800));
                    if (voiceIsConnected() && !isTourRunning()) {
                        finished = await startExhibitionTour(tourCtx);
                    }
                }
                if (!finished) autoStartedRef.current = false;
            } finally {
                useTutorialStore.getState().setRunning(false);
            }
        })();
    }, [
        isExhibition,
        hasFetchedDesign,
        selectedDesignId,
        exhibitionSession?.demoDesignId,
        exhibitionSession?.tourDismissed,
    ]);

    const live = connected || connecting;
    // Exhibition demo design only — never in normal mode. Visible while the mic
    // is open; closing the mic (or Skip, which ends the session) hides them, and
    // opening the mic again brings them back, same idea as the old showcase box.
    const showTourControls =
        connected
        && isExhibition
        && isExhibitionTourDesign(selectedDesignId, exhibitionSession?.demoDesignId);

    // Labels follow what the buttons will actually act on, so Replay does not
    // offer to repeat the tour while it is the walkthrough that is speaking.
    const narration = useTutorialStore((state) => state.narration);
    const replayLabel = narration === "tutorial" ? "Replay walkthrough" : "Replay tour";
    const skipLabel = narration === "tutorial" ? "Skip walkthrough" : "Skip tour";

    const onTourReplay = () => {
        requestExhibitionTour(true);
    };
    const onTourSkip = () => {
        skipExhibitionTour();
    };
    const selectValue = voices.some((voice) => voice.id === selectedVoiceId)
        ? selectedVoiceId
        : DEFAULT_VOICE_ID;
    const selectedIndex = Math.max(0, voices.findIndex((voice) => voice.id === selectValue));
    const shownIndex = voices[voiceCenter] ? voiceCenter : selectedIndex;
    const selectedVoice = voices[shownIndex];
    const leftVoice = voices.length > 1 ? voices[(shownIndex - 1 + voices.length) % voices.length] : undefined;
    const rightVoice = voices.length > 1 ? voices[(shownIndex + 1) % voices.length] : undefined;

    const realVoiceIndex = (cloneIndex: number) => {
        const count = voices.length;
        if (count <= 0) return 0;
        return ((cloneIndex % count) + count) % count;
    };

    const middleCloneStart = () => voices.length * Math.floor(VOICE_LOOP_COPIES / 2);

    const indexFromStripScroll = (node: HTMLElement) => {
        const mid = node.scrollLeft + node.clientWidth / 2;
        let best = 0;
        let bestDist = Number.POSITIVE_INFINITY;
        Array.from(node.children).forEach((child, index) => {
            const item = child as HTMLElement;
            const center = item.offsetLeft + item.offsetWidth / 2;
            const dist = Math.abs(center - mid);
            if (dist < bestDist) {
                bestDist = dist;
                best = index;
            }
        });
        return best;
    };

    const scrollStripToClone = (node: HTMLElement, cloneIndex: number) => {
        const item = node.children[cloneIndex] as HTMLElement | undefined;
        if (!item) return;
        node.scrollLeft = item.offsetLeft - (node.clientWidth - item.offsetWidth) / 2;
    };

    const wrapStripClone = (node: HTMLElement, cloneIndex: number) => {
        const count = voices.length;
        if (count < 2) return cloneIndex;
        const copy = Math.floor(cloneIndex / count);
        if (copy >= 2 && copy <= VOICE_LOOP_COPIES - 3) return cloneIndex;
        const next = middleCloneStart() + realVoiceIndex(cloneIndex);
        scrollStripToClone(node, next);
        return next;
    };

    const pickVoiceDelta = (delta: number) => {
        if (voices.length < 2) return;
        skipStripScrollRef.current = true;
        if (stripScrollTimerRef.current != null) {
            window.clearTimeout(stripScrollTimerRef.current);
            stripScrollTimerRef.current = null;
        }
        const node = voiceStripRef.current;
        let clone = node ? indexFromStripScroll(node) : middleCloneStart() + shownIndex;
        clone += delta;
        const next = realVoiceIndex(clone);
        const voice = voices[next];
        if (!voice) return;
        syncVoiceCenter(next);
        void onSelectVoice(voice.id);
        if (node) {
            if (clone < 0 || clone >= voices.length * VOICE_LOOP_COPIES) {
                clone = middleCloneStart() + next;
            }
            scrollStripToClone(node, clone);
            wrapStripClone(node, clone);
        }
        window.setTimeout(() => {
            skipStripScrollRef.current = false;
        }, 160);
    };

    const onStripPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        stripTapRef.current = {
            x: event.clientX,
            y: event.clientY,
            scroll: event.currentTarget.scrollLeft,
        };
    };

    const onStripPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        const start = stripTapRef.current;
        stripTapRef.current = null;
        if (!start || !voiceStripReadyRef.current) return;
        const node = event.currentTarget;
        const dx = Math.abs(event.clientX - start.x);
        const dy = Math.abs(event.clientY - start.y);
        const ds = Math.abs(node.scrollLeft - start.scroll);
        if (dx > 8 || dy > 8 || ds > 4) return;
        const rect = node.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const mid = rect.width / 2;
        if (x < mid - 36) pickVoiceDelta(-1);
        else if (x > mid + 36) pickVoiceDelta(1);
    };

    const onSidePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        sideDragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startScroll: voiceStripRef.current?.scrollLeft ?? 0,
            moved: false,
        };
    };

    const onSidePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
        const drag = sideDragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const dx = event.clientX - drag.startX;
        if (Math.abs(dx) > 8) drag.moved = true;
        if (!drag.moved) return;
        const node = voiceStripRef.current;
        if (node) node.scrollLeft = drag.startScroll - dx;
    };

    const onSidePointerEnd = () => {
        const drag = sideDragRef.current;
        sideDragRef.current = null;
        if (drag?.moved) skipSideClickRef.current = true;
    };

    const onSideClick = (delta: number) => {
        if (skipSideClickRef.current) {
            skipSideClickRef.current = false;
            return;
        }
        pickVoiceDelta(delta);
    };

    const syncVoiceCenter = React.useCallback((index: number) => {
        const next = Math.max(0, Math.min(voices.length - 1, index));
        setVoiceCenter(next);
        return next;
    }, [voices.length]);

    React.useLayoutEffect(() => {
        if (live || voices.length < 2) return;
        const index = Math.max(0, voices.findIndex((voice) => voice.id === selectValue));
        voiceStripReadyRef.current = false;
        setVoiceCenter(index);
        const node = voiceStripRef.current;
        if (node) {
            scrollStripToClone(node, middleCloneStart() + index);
        }
        const timer = window.setTimeout(() => {
            voiceStripReadyRef.current = true;
        }, 80);
        return () => window.clearTimeout(timer);
    }, [live, voices.length]);

  return (
        <div
            className={`voice-orb-root${connected ? " live" : ""}${speaking ? " speaking" : ""}${clippingActive ? " clipping-active" : ""}${showTourControls ? " tour-controls" : ""}`}
            style={{ ['--voice-level' as string]: level }}
        >
            <div className="voice-dock">
                <div className="voice-orb-stage">
                    {connected && (
                        <svg className="voice-halo" viewBox="0 0 100 100" aria-hidden>
                            <defs>
                                <linearGradient id={`voice-halo-grad-${haloId}`} x1="8%" y1="12%" x2="92%" y2="88%">
                                    <stop offset="0%" stopColor="#FF2D95" />
                                    <stop offset="48%" stopColor="#A14BFF" />
                                    <stop offset="100%" stopColor="#00C2FF" />
                                </linearGradient>
                            </defs>
                            {Array.from({ length: HALO_WAVES }, (_, wave) => (
                                <path
                                    key={wave}
                                    ref={(node) => {
                                        haloPathEls.current[wave] = node;
                                    }}
                                    d={buildHaloWave(wave, 0.06, idleBands(), 0)}
                                    fill="none"
                                    stroke={`url(#voice-halo-grad-${haloId})`}
                                    strokeWidth={0.38 + (wave % 4) * 0.08}
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                    opacity={0.28 + (wave % 5) * 0.07}
                                />
                            ))}
                        </svg>
                    )}
                    <div className={`voice-mic-row${showTourControls ? " has-tour-controls" : ""}`}>
                        {showTourControls && (
                            <button
                                type="button"
                                className="voice-tour-btn"
                                onClick={onTourReplay}
                                aria-label={replayLabel}
                                title={replayLabel}
                            >
                                <ReplayIcon fontSize="small" />
                            </button>
                        )}
                        <div className={`voice-cluster${live ? " is-on" : " is-off"}`}>
                            <span className="voice-blob" aria-hidden>
                                <span className="voice-blob-core" />
                            </span>
                            <button
                                type="button"
                                className="voice-mic"
                                onClick={onToggleVoice}
                                disabled={connecting}
                                aria-label={connected ? "Stop voice agent" : "Start voice agent"}
                                title={
                                    !connected
                                        ? "Talk to the insole agent"
                                        : micMuted
                                            ? "Listening is paused while the tour is speaking"
                                            : "Stop"
                                }
                            >
                                {connecting ? <CircularProgress size={22} color="inherit" /> : <VoiceMicIcon muted={!connected} />}
                            </button>
                        </div>
                        {showTourControls && (
                            <button
                                type="button"
                                className="voice-tour-btn"
                                onClick={onTourSkip}
                                aria-label={skipLabel}
                                title={skipLabel}
                            >
                                <CloseIcon fontSize="small" />
                            </button>
                        )}
                    </div>
                    {!live && voices.length > 1 && (
                        <>
                            <div className="voice-strip" aria-hidden>
                                <div
                                    className="voice-strip-list"
                                    ref={voiceStripRef}
                                    onPointerDown={onStripPointerDown}
                                    onPointerUp={onStripPointerUp}
                                    onPointerCancel={() => {
                                        stripTapRef.current = null;
                                    }}
                                    onScroll={(event) => {
                                        if (skipStripScrollRef.current || !voiceStripReadyRef.current) return;
                                        const node = event.currentTarget;
                                        const clone = wrapStripClone(node, indexFromStripScroll(node));
                                        syncVoiceCenter(realVoiceIndex(clone));
                                        if (stripScrollTimerRef.current != null) {
                                            window.clearTimeout(stripScrollTimerRef.current);
                                        }
                                        stripScrollTimerRef.current = window.setTimeout(() => {
                                            const settledClone = wrapStripClone(node, indexFromStripScroll(node));
                                            const settled = syncVoiceCenter(realVoiceIndex(settledClone));
                                            const voice = voices[settled];
                                            if (voice && voice.id !== selectValue) void onSelectVoice(voice.id);
                                        }, 80);
                                    }}
                                >
                                    {Array.from({ length: VOICE_LOOP_COPIES }, (_, copy) =>
                                        voices.map((voice) => (
                                            <div key={`${copy}-${voice.id}`} className="voice-strip-item" />
                                        ))
                                    )}
                                </div>
                            </div>
                            {selectedVoice && (
                                <span className="voice-pick-top">{selectedVoice.label}</span>
                            )}
                            {leftVoice && (
                                <button
                                    type="button"
                                    className="voice-pick-side is-left"
                                    onPointerDown={onSidePointerDown}
                                    onPointerMove={onSidePointerMove}
                                    onPointerUp={onSidePointerEnd}
                                    onPointerCancel={onSidePointerEnd}
                                    onClick={() => onSideClick(-1)}
                                    aria-label={`Select ${leftVoice.label}`}
                                >
                                    <span>{leftVoice.label}</span>
                                    <span className="voice-pick-line" aria-hidden />
                                </button>
                            )}
                            {rightVoice && (
                                <button
                                    type="button"
                                    className="voice-pick-side is-right"
                                    onPointerDown={onSidePointerDown}
                                    onPointerMove={onSidePointerMove}
                                    onPointerUp={onSidePointerEnd}
                                    onPointerCancel={onSidePointerEnd}
                                    onClick={() => onSideClick(1)}
                                    aria-label={`Select ${rightVoice.label}`}
                                >
                                    <span className="voice-pick-line" aria-hidden />
                                    <span>{rightVoice.label}</span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

function waitUntil(isReady: () => boolean, ms: number, message: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (isReady()) {
            resolve();
            return;
        }
        const started = Date.now();
        const timer = window.setInterval(() => {
            if (isReady()) {
                window.clearInterval(timer);
                resolve();
                return;
            }
            if (Date.now() - started >= ms) {
                window.clearInterval(timer);
                reject(new Error(message));
            }
        }, 100);
    });
}

function safeNumber(read?: () => number): number {
    try {
        const value = read?.();
        return Number.isFinite(value) ? Math.min(1, Math.max(0, value as number)) : 0;
    } catch {
        return 0;
    }
}

function sampleBands(read?: () => Uint8Array): number[] {
    try {
        const data = read?.();
        if (!data || data.length === 0) return idleBands();
        const size = Math.max(1, Math.floor((data.length * 0.55) / HALO_BANDS));
        return Array.from({ length: HALO_BANDS }, (_, index) => {
            let peak = 0;
            const start = index * size;
            for (let i = 0; i < size; i += 1) peak = Math.max(peak, data[start + i] || 0);
            return Math.min(1, Math.pow(peak / 165, 0.72));
        });
    } catch {
        return idleBands();
    }
}

function idleBands(): number[] {
    return Array.from({ length: HALO_BANDS }, () => 0.04);
}

function buildHaloWave(wave: number, level: number, bands: number[], phase: number): string {
    const freq = 20 + (wave % 8) * 2;
    const baseR = HALO_BASE + (wave % 5) * 0.22;
    const peak = 0.22 + level * 13.5;
    const wavePhase = phase * (1 + wave * 0.06) + wave * 0.85;
    let path = "";
    for (let i = 0; i <= HALO_POINTS; i += 1) {
        const t = i / HALO_POINTS;
        const angle = t * Math.PI * 2;
        const band = bands[Math.min(bands.length - 1, Math.floor(t * bands.length))] ?? 0;
        const drive = Math.min(1, level * 0.55 + band * 0.9);
        const amp = Math.min(6.2, peak * (0.12 + 0.88 * drive));
        const r =
            baseR +
            amp * Math.sin(freq * angle + wavePhase) +
            amp * 0.38 * Math.sin(freq * 0.5 * angle - wavePhase * 1.15);
        const x = 50 + Math.cos(angle) * r;
        const y = 50 + Math.sin(angle) * r;
        path += `${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`;
    }
    return `${path} Z`;
}

function VoiceMicIcon({ muted }: { muted: boolean }): React.ReactElement {
    const body =
        "M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z";
    return (
        <svg className="voice-mic-icon" viewBox="0 0 24 24" aria-hidden>
            <path className="voice-mic-icon-shade" d={body} />
            <path fill="currentColor" d={body} />
            {muted && (
                <line
                    x1="4.1"
                    y1="19.6"
                    x2="19.9"
                    y2="4.4"
                    stroke="currentColor"
                    strokeWidth="2.3"
                    strokeLinecap="round"
                />
            )}
        </svg>
    );
}

const Chatbox: React.FC = () => (
    <ConversationProvider>
        <ChatboxInner />
    </ConversationProvider>
);

export default Chatbox;
